/**
 * Fleet Multi-Agent Profile Quick-Switcher
 *
 * Compact popover/dropdown for quickly:
 * - Viewing the current active profile name
 * - Enabling/disabling multi-agent mode
 * - Switching between execution profiles
 * - Accessing the full Agents page
 *
 * Placement: rendered inside the chat toolbar / header area.
 */

import { html, nothing } from "lit";
import { repeat } from "lit/directives/repeat.js";
import type { ExecutionProfile } from "../types.ts";

// ─── Icons ────────────────────────────────────────────────────────────────────

const multiAgentIcon = html`
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
`;

const singleAgentIcon = html`
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
`;

const checkIcon = html`
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2.5"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
`;

const switchIcon = html`
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <polyline points="17 1 21 5 17 9" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <polyline points="7 23 3 19 7 15" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
`;

const externalLinkIcon = html`
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
`;

// ─── Props ─────────────────────────────────────────────────────────────────────

export interface FleetMultiAgentSwitcherProps {
  open: boolean;
  profiles: ExecutionProfile[];
  activeProfileId: string | null;
  loading: boolean;
  error: string | null;
  onSwitchProfile: (profileId: string) => void;
  onToggleMultiAgent: (enabled: boolean) => void;
  onClose: () => void;
  onOpenAgentsPage: () => void;
}

// ─── Renderer ─────────────────────────────────────────────────────────────────

export function renderFleetMultiAgentSwitcher(props: FleetMultiAgentSwitcherProps) {
  const {
    open,
    profiles,
    activeProfileId,
    loading,
    error,
    onSwitchProfile,
    onToggleMultiAgent,
    onClose,
    onOpenAgentsPage,
  } = props;

  if (!open) return nothing;

  const activeProfile = profiles.find((p) => p.id === activeProfileId) ?? profiles[0];
  const isMultiAgent = activeProfile?.isMultiAgent ?? false;

  return html`
    <!-- Backdrop -->
    <div
      class="fleet-ma-backdrop"
      @click=${onClose}
      @keydown=${(e: KeyboardEvent) => {
        if (e.key === "Escape") onClose();
      }}
      aria-label="Close multi-agent switcher"
    ></div>

    <!-- Popover panel -->
    <div
      class="fleet-ma-popover"
      role="dialog"
      aria-label="Multi-agent profile switcher"
      aria-modal="true"
      @click=${(e: Event) => e.stopPropagation()}
    >
      <!-- Header -->
      <div class="fleet-ma-header">
        <div class="fleet-ma-header__icon ${isMultiAgent ? "fleet-ma-header__icon--active" : ""}">
          ${isMultiAgent ? multiAgentIcon : singleAgentIcon}
        </div>
        <div class="fleet-ma-header__text">
          <div class="fleet-ma-header__title">Multi-Agent</div>
          <div class="fleet-ma-header__subtitle">
            ${activeProfile ? activeProfile.name : "Loading…"}
          </div>
        </div>
        <div class="fleet-ma-status-dot ${isMultiAgent ? "fleet-ma-status-dot--on" : ""}"
             title=${isMultiAgent ? "Multi-agent active" : "Single-agent mode"}>
        </div>
      </div>

      <!-- Error -->
      ${error ? html`<div class="fleet-ma-error">${error}</div>` : nothing}

      <!-- Loading skeleton -->
      ${
        loading && profiles.length === 0
          ? html`
              <div class="fleet-ma-loading">
                <div class="fleet-ma-skeleton"></div>
                <div class="fleet-ma-skeleton fleet-ma-skeleton--short"></div>
              </div>
            `
          : nothing
      }

      <!-- Profile list -->
      ${
        !loading || profiles.length > 0
          ? html`
        <div class="fleet-ma-section">
          <div class="fleet-ma-section-label">Switch Profile</div>
          ${repeat(
            profiles,
            (p) => p.id,
            (p) => {
              const isActive = p.id === activeProfileId;
              return html`
                <button
                  type="button"
                  class="fleet-ma-profile-btn ${isActive ? "fleet-ma-profile-btn--active" : ""}"
                  @click=${() => onSwitchProfile(p.id)}
                  title=${p.description ?? p.name}
                >
                  <span class="fleet-ma-profile-btn__icon">
                    ${p.isMultiAgent ? multiAgentIcon : singleAgentIcon}
                  </span>
                  <span class="fleet-ma-profile-btn__info">
                    <span class="fleet-ma-profile-btn__name">${p.name}</span>
                    ${p.description ? html`<span class="fleet-ma-profile-btn__desc">${p.description}</span>` : nothing}
                  </span>
                  ${
                    isActive
                      ? html`<span class="fleet-ma-profile-btn__check">${checkIcon}</span>`
                      : nothing
                  }
                </button>
              `;
            },
          )}
        </div>

        <!-- Toggle multi-agent -->
        <div class="fleet-ma-section">
          <div class="fleet-ma-section-label">Mode</div>
          <div class="fleet-ma-toggle-row">
            <button
              type="button"
              class="fleet-ma-toggle-btn ${!isMultiAgent ? "fleet-ma-toggle-btn--active" : ""}"
              @click=${() => onToggleMultiAgent(false)}
              title="Switch to single-agent mode"
            >
              ${singleAgentIcon}
              <span>Single-Agent</span>
            </button>
            <button
              type="button"
              class="fleet-ma-toggle-btn ${isMultiAgent ? "fleet-ma-toggle-btn--active" : ""}"
              @click=${() => onToggleMultiAgent(true)}
              title="Switch to multi-agent mode"
            >
              ${multiAgentIcon}
              <span>Multi-Agent</span>
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="fleet-ma-footer">
          <button
            type="button"
            class="fleet-ma-footer-btn"
            @click=${onOpenAgentsPage}
          >
            ${externalLinkIcon}
            <span>Manage in Agents Page</span>
          </button>
        </div>
      `
          : nothing
      }
    </div>
  `;
}
